import React, { useState, useEffect, useRef } from 'react';
import tripshareService from '../../../services/tripshareService';
import './TripShare.css';

const TripShare = () => {
    const [activeTab, setActiveTab] = useState('chat');
    const [sessionId, setSessionId] = useState('');
    const [messages, setMessages] = useState([]);
    const [currentMessage, setCurrentMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [myTrips, setMyTrips] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [popularDestinations, setPopularDestinations] = useState([]);
    const [bookmarkedTrips, setBookmarkedTrips] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [showTripModal, setShowTripModal] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        // Generate session ID on component mount
        setSessionId(tripshareService.generateSessionId());
        
        // Add initial AI greeting
        setMessages([{
            role: 'assistant',
            content: "Welcome to TripShare! 🌍 I'm here to help you connect with other agents' travel experiences. You can:\n\n✈️ **Share your own travel experiences** - Help other agents by sharing insights from trips you've taken or planned\n\n🔍 **Discover recommendations** - Find hotels, restaurants, activities, and supplier tips from other agents' experiences\n\nWhat would you like to do today?",
            timestamp: new Date(),
            actions: [
                { type: 'button', text: 'Share a Trip Experience', value: 'share_trip' },
                { type: 'button', text: 'Find Travel Recommendations', value: 'find_recommendations' }
            ]
        }]);

        // Load initial data
        loadInitialData();
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const loadInitialData = async () => {
        try {
            const [myTripsData, popularData, bookmarksData, analyticsData] = await Promise.all([
                tripshareService.getMyTrips(),
                tripshareService.getPopularDestinations(),
                tripshareService.getBookmarkedTrips(),
                tripshareService.getAnalytics()
            ]);

            setMyTrips(myTripsData.trips || []);
            setPopularDestinations(popularData.destinations || []);
            setBookmarkedTrips(bookmarksData.bookmarks || []);
            setAnalytics(analyticsData.analytics || null);
        } catch (error) {
            console.error('Error loading initial data:', error);
        }
    };

    const handleSendMessage = async (message = currentMessage) => {
        if (!message.trim() || isLoading) return;

        const userMessage = {
            role: 'user',
            content: message,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setCurrentMessage('');
        setIsLoading(true);

        try {
            const response = await tripshareService.sendChatMessage(message, sessionId);
            
            const aiMessage = {
                role: 'assistant',
                content: response.message,
                timestamp: new Date(),
                actions: response.actions || []
            };

            setMessages(prev => [...prev, aiMessage]);

            // Reload data if trip was saved
            if (response.conversationType === 'sharing' && response.currentStep === 'save_trip') {
                await loadInitialData();
            }

        } catch (error) {
            console.error('Error sending message:', error);
            const errorMessage = {
                role: 'assistant',
                content: "I'm having trouble processing your request right now. Please try again in a moment.",
                timestamp: new Date(),
                actions: []
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleActionClick = (action) => {
        if (action.type === 'button') {
            handleSendMessage(action.value);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleTripClick = async (tripId) => {
        try {
            const tripData = await tripshareService.getTripDetails(tripId);
            setSelectedTrip(tripData);
            setShowTripModal(true);
        } catch (error) {
            console.error('Error loading trip details:', error);
        }
    };

    const handleMarkHelpful = async (tripId) => {
        try {
            await tripshareService.markTripHelpful(tripId);
            // Refresh the trip details to show updated helpful count
            if (selectedTrip && selectedTrip.trip.id === tripId) {
                const updatedTrip = await tripshareService.getTripDetails(tripId);
                setSelectedTrip(updatedTrip);
            }
        } catch (error) {
            console.error('Error marking trip helpful:', error);
        }
    };

    const handleBookmarkTrip = async (tripId) => {
        try {
            await tripshareService.bookmarkTrip(tripId);
            // Refresh bookmarks
            const bookmarksData = await tripshareService.getBookmarkedTrips();
            setBookmarkedTrips(bookmarksData.bookmarks || []);
        } catch (error) {
            console.error('Error bookmarking trip:', error);
        }
    };

    const renderMessage = (message, index) => (
        <div key={index} className={`message ${message.role}`}>
            <div className="message-content">
                <div className="message-text">
                    {message.content.split('\n').map((line, i) => (
                        <div key={i}>
                            {line.startsWith('**') && line.endsWith('**') ? (
                                <strong>{line.slice(2, -2)}</strong>
                            ) : line.startsWith('✈️') || line.startsWith('🔍') || line.startsWith('📍') || 
                                   line.startsWith('👤') || line.startsWith('📅') || line.startsWith('📝') || 
                                   line.startsWith('👍') || line.startsWith('🎉') || line.startsWith('✅') ? (
                                <span className="message-highlight">{line}</span>
                            ) : (
                                line
                            )}
                        </div>
                    ))}
                </div>
                {message.actions && message.actions.length > 0 && (
                    <div className="message-actions">
                        {message.actions.map((action, i) => (
                            action.type === 'button' ? (
                                <button
                                    key={i}
                                    className="action-button"
                                    onClick={() => handleActionClick(action)}
                                >
                                    {action.text}
                                </button>
                            ) : action.type === 'input' ? (
                                <div key={i} className="action-input-container">
                                    <input
                                        type="text"
                                        placeholder={action.placeholder}
                                        className="action-input"
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                handleSendMessage(e.target.value);
                                                e.target.value = '';
                                            }
                                        }}
                                    />
                                </div>
                            ) : action.type === 'textarea' ? (
                                <div key={i} className="action-textarea-container">
                                    <textarea
                                        placeholder={action.placeholder}
                                        className="action-textarea"
                                        rows="3"
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && e.ctrlKey) {
                                                handleSendMessage(e.target.value);
                                                e.target.value = '';
                                            }
                                        }}
                                    />
                                    <small className="textarea-hint">Press Ctrl+Enter to send</small>
                                </div>
                            ) : null
                        ))}
                    </div>
                )}
                <div className="message-timestamp">
                    {message.timestamp.toLocaleTimeString()}
                </div>
            </div>
        </div>
    );

    const renderTripCard = (trip, showActions = true) => (
        <div key={trip.id} className="trip-card" onClick={() => handleTripClick(trip.id)}>
            <div className="trip-header">
                <h4 className="trip-title">{trip.title}</h4>
                <span className="trip-type">{trip.trip_type}</span>
            </div>
            <div className="trip-destination">📍 {trip.destination}</div>
            {trip.travel_dates_start && (
                <div className="trip-dates">
                    📅 {new Date(trip.travel_dates_start).toLocaleDateString()}
                </div>
            )}
            {trip.summary && (
                <div className="trip-summary">
                    {trip.summary.length > 150 ? trip.summary.substring(0, 150) + '...' : trip.summary}
                </div>
            )}
            <div className="trip-stats">
                <span>👁️ {trip.view_count || 0} views</span>
                <span>👍 {trip.helpful_count || 0} helpful</span>
                {trip.photo_count && <span>📸 {trip.photo_count} photos</span>}
            </div>
            {trip.first_name && (
                <div className="trip-author">
                    By {trip.first_name} {trip.last_name} ({trip.agency_name})
                </div>
            )}
            {showActions && (
                <div className="trip-actions" onClick={(e) => e.stopPropagation()}>
                    <button 
                        className="btn-helpful"
                        onClick={() => handleMarkHelpful(trip.id)}
                    >
                        👍 Helpful
                    </button>
                    <button 
                        className="btn-bookmark"
                        onClick={() => handleBookmarkTrip(trip.id)}
                    >
                        🔖 Bookmark
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <div className="tripshare-container">
            <div className="tripshare-header">
                <h1>🌍 TripShare</h1>
                <p>Share experiences, discover insights, help fellow agents</p>
            </div>

            <div className="tripshare-tabs">
                <button 
                    className={`tab ${activeTab === 'chat' ? 'active' : ''}`}
                    onClick={() => setActiveTab('chat')}
                >
                    💬 AI Assistant
                </button>
                <button 
                    className={`tab ${activeTab === 'my-trips' ? 'active' : ''}`}
                    onClick={() => setActiveTab('my-trips')}
                >
                    ✈️ My Trips ({myTrips.length})
                </button>
                <button 
                    className={`tab ${activeTab === 'discover' ? 'active' : ''}`}
                    onClick={() => setActiveTab('discover')}
                >
                    🔍 Discover
                </button>
                <button 
                    className={`tab ${activeTab === 'bookmarks' ? 'active' : ''}`}
                    onClick={() => setActiveTab('bookmarks')}
                >
                    🔖 Bookmarks ({bookmarkedTrips.length})
                </button>
            </div>

            <div className="tripshare-content">
                {activeTab === 'chat' && (
                    <div className="chat-container">
                        <div className="chat-messages">
                            {messages.map((message, index) => renderMessage(message, index))}
                            {isLoading && (
                                <div className="message assistant">
                                    <div className="message-content">
                                        <div className="typing-indicator">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                        <div className="chat-input-container">
                            <textarea
                                value={currentMessage}
                                onChange={(e) => setCurrentMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Ask me about sharing a trip or finding recommendations..."
                                className="chat-input"
                                rows="2"
                                disabled={isLoading}
                            />
                            <button 
                                onClick={() => handleSendMessage()}
                                className="send-button"
                                disabled={isLoading || !currentMessage.trim()}
                            >
                                Send
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'my-trips' && (
                    <div className="trips-container">
                        <div className="trips-header">
                            <h3>Your Shared Experiences</h3>
                            {analytics && (
                                <div className="analytics-summary">
                                    <span>📊 {analytics.trip_stats.total_trips} trips shared</span>
                                    <span>👁️ {analytics.trip_stats.total_views} total views</span>
                                    <span>👍 {analytics.trip_stats.total_helpful} helpful marks</span>
                                </div>
                            )}
                        </div>
                        <div className="trips-grid">
                            {myTrips.length > 0 ? (
                                myTrips.map(trip => renderTripCard(trip, false))
                            ) : (
                                <div className="empty-state">
                                    <p>You haven't shared any trips yet!</p>
                                    <button 
                                        className="btn-primary"
                                        onClick={() => setActiveTab('chat')}
                                    >
                                        Share Your First Trip
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'discover' && (
                    <div className="discover-container">
                        <div className="popular-destinations">
                            <h3>Popular Destinations</h3>
                            <div className="destinations-grid">
                                {popularDestinations.map((dest, index) => (
                                    <div key={index} className="destination-card">
                                        <h4>{dest.destination}</h4>
                                        <div className="destination-stats">
                                            <span>✈️ {dest.trip_count} experiences</span>
                                            <span>👁️ {dest.total_views} views</span>
                                            <span>⭐ {Math.round(dest.avg_helpful * 10) / 10} avg helpful</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <div className="search-prompt">
                            <h3>Find Specific Recommendations</h3>
                            <p>Use the AI Assistant to search for specific destinations, hotels, restaurants, or activities!</p>
                            <button 
                                className="btn-primary"
                                onClick={() => {
                                    setActiveTab('chat');
                                    handleSendMessage('find_recommendations');
                                }}
                            >
                                Start Searching
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'bookmarks' && (
                    <div className="bookmarks-container">
                        <h3>Your Bookmarked Trips</h3>
                        <div className="trips-grid">
                            {bookmarkedTrips.length > 0 ? (
                                bookmarkedTrips.map(trip => (
                                    <div key={trip.id} className="trip-card bookmarked">
                                        {renderTripCard(trip, true)}
                                        {trip.bookmark_notes && (
                                            <div className="bookmark-notes">
                                                📝 {trip.bookmark_notes}
                                            </div>
                                        )}
                                        <div className="bookmark-date">
                                            Bookmarked: {new Date(trip.bookmarked_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="empty-state">
                                    <p>No bookmarked trips yet!</p>
                                    <button 
                                        className="btn-primary"
                                        onClick={() => setActiveTab('discover')}
                                    >
                                        Discover Trips
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Trip Details Modal */}
            {showTripModal && selectedTrip && (
                <div className="modal-overlay" onClick={() => setShowTripModal(false)}>
                    <div className="trip-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{selectedTrip.trip.title}</h2>
                            <button 
                                className="modal-close"
                                onClick={() => setShowTripModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-content">
                            <div className="trip-details">
                                <div className="detail-section">
                                    <h4>📍 Destination</h4>
                                    <p>{selectedTrip.trip.destination}</p>
                                </div>
                                
                                {selectedTrip.trip.summary && (
                                    <div className="detail-section">
                                        <h4>📝 Overview</h4>
                                        <p>{selectedTrip.trip.summary}</p>
                                    </div>
                                )}
                                
                                {selectedTrip.trip.hotels && (
                                    <div className="detail-section">
                                        <h4>🏨 Hotels</h4>
                                        <p>{selectedTrip.trip.hotels}</p>
                                    </div>
                                )}
                                
                                {selectedTrip.trip.restaurants && (
                                    <div className="detail-section">
                                        <h4>🍽️ Restaurants</h4>
                                        <p>{selectedTrip.trip.restaurants}</p>
                                    </div>
                                )}
                                
                                {selectedTrip.trip.activities && (
                                    <div className="detail-section">
                                        <h4>🎯 Activities</h4>
                                        <p>{selectedTrip.trip.activities}</p>
                                    </div>
                                )}
                                
                                {selectedTrip.trip.suppliers_used && (
                                    <div className="detail-section">
                                        <h4>🔗 Suppliers & Booking Tips</h4>
                                        <p>{selectedTrip.trip.suppliers_used}</p>
                                    </div>
                                )}
                                
                                {selectedTrip.trip.insider_tips && (
                                    <div className="detail-section">
                                        <h4>💡 Insider Tips</h4>
                                        <p>{selectedTrip.trip.insider_tips}</p>
                                    </div>
                                )}
                                
                                <div className="trip-meta">
                                    <p><strong>Shared by:</strong> {selectedTrip.trip.first_name} {selectedTrip.trip.last_name} ({selectedTrip.trip.agency_name})</p>
                                    <p><strong>Trip Type:</strong> {selectedTrip.trip.trip_type}</p>
                                    <p><strong>Views:</strong> {selectedTrip.trip.view_count} | <strong>Helpful:</strong> {selectedTrip.trip.helpful_count}</p>
                                </div>
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button 
                                className="btn-helpful"
                                onClick={() => handleMarkHelpful(selectedTrip.trip.id)}
                            >
                                👍 Mark as Helpful
                            </button>
                            <button 
                                className="btn-bookmark"
                                onClick={() => handleBookmarkTrip(selectedTrip.trip.id)}
                            >
                                🔖 Bookmark Trip
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TripShare;
